import { useLayoutEffect, useState, useEffect, useRef } from "react";
import { Stack } from "@fluentui/react";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import "../../css/superTreeview.css";
import "../../css/treeview.css";
import Loader from "../LoadSpinner";
import { IconButton } from "@fluentui/react/lib/Button";
import { Label } from "@fluentui/react";
import "react-dropdown-tree-select/dist/styles.css";
import "../../css/dropDownTree.css";
import { useTheme } from "../../theme/OAS365Theme";
import "../../scss/DropDown.scss";

import {
  IFS_SERVER_ERROR,
  NO_CLASSFORMAT_FOUND,
  NO_FORMATS_FOUND,
  CHECKIN_VALIDATION_CLASS_REQUIRED,
  CHECKIN_VALIDATION_FORMAT_REQUIRED,
} from "../../redux/constants/messageTypes";
import { isValidPlayload } from "../../functions/reduxMethod";

import {
  ON_CHECKIN_NEW_STATUS,
  DATA_LOAD_STATUS,
  REFRESH_TOKEN_ACTION,
  RESPONSE_TYPE,
} from "../../redux/constants/metaData";
import { useCheckin } from "../../redux/hooks/useCheckin";
import { useMetadata } from "../../redux/hooks/useMetadata";
import { useTools } from "../../redux/hooks/useTools";
import FieldValidationMessage from "./FieldValidationMessage";

let classCode = "";
let formatCode = "";
let formatCodeDisplay = "";
let viewRequired = "N";
let objConRequired = "N";
let formatRequired = "N";
let classCodeChecked = null;
let formatCodeChecked = null;
let classFormatsCache = {};

export let cleanAll = () => {
  classCode = "";
  formatCode = "";
  formatCodeDisplay = "";
  viewRequired = "N";
  objConRequired = "N";
  formatRequired = "N";
  classCodeChecked = null;
  formatCodeChecked = null;
  classFormatsCache = {};
};

const ClassFormatTreeLoad = ({
  onLoadingStateChange,
  showFieldValidation = true,
  /** When true, force the class combo box into its loading state (IFS autopopulation). */
  isLoadingOverride = false,
} = {}) => {
  const {
    uploadFiles,
    docClass,
    docFormat,
    formatRequired: isFormatRequired,
    docClassFormatList,
    checkinNewStatus,
    uploadViewCopyCount,
    basicDataProgressType,
    onBasicDataLoad,
    onGetFormatsByClass,
  } = useCheckin();
  const { tokenRefreshPreActionInvoke, messageBoxInfo } = useMetadata();
  const {
    onDocClassFormatChange,
    onDocClassFormatListChange,
    onCheckinNewStatusChange,
    onBasicDataProgressChange,
  } = useTools();
  const binIcon = { iconName: "Refresh" };
  const theme = useTheme();

  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [classOptions, setClassOptions] = useState([]);
  const [formatOptions, setFormatOptions] = useState([]);
  const [formattedData, setFormattedData] = useState(null);
  const prevClassRef = useRef(null);
  const skipNextSyncRef = useRef(false);
  const basicDataReloadRequestedRef = useRef(false);

  const [filteredClassOptions, setFilteredClassOptions] = useState([]);
  const [filteredFormatOptions, setFilteredFormatOptions] = useState([]);
  const [classSearchValue, setClassSearchValue] = useState("");
  const [formatSearchValue, setFormatSearchValue] = useState("");
  const [classMenuOpen, setClassMenuOpen] = useState(false);
  const [formatMenuOpen, setFormatMenuOpen] = useState(false);
  const [isClassFormatsLoading, setIsClassFormatsLoading] = useState(false);

  const NO_FORMAT_AVAILABLE_KEY = "no-format";
  const NO_RESULTS_CLASS_KEY = "no-results-class";
  const NO_RESULTS_FORMAT_KEY = "no-results-format";

  const isBasicDataLoading =
    basicDataProgressType === DATA_LOAD_STATUS.onStart ||
    basicDataProgressType === DATA_LOAD_STATUS.onLoad;

  const hasClassFormatOptions = classOptions.length > 0;

  const isClassListLoading =
    isBasicDataLoading ||
    (basicDataProgressType === DATA_LOAD_STATUS.completed &&
      !hasClassFormatOptions) ||
    isLoadingOverride === true;

  const isTerminalBasicDataState =
    basicDataProgressType === DATA_LOAD_STATUS.noDate ||
    basicDataProgressType === DATA_LOAD_STATUS.error ||
    basicDataProgressType === DATA_LOAD_STATUS.ifsError;

  const shouldShowClassFormatLoader =
    !isTerminalBasicDataState && isClassListLoading;

  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(shouldShowClassFormatLoader);
    }
  }, [shouldShowClassFormatLoader, onLoadingStateChange]);

  useEffect(() => {
    if (basicDataProgressType === DATA_LOAD_STATUS.onStart) {
      onBasicDataProgressChange(DATA_LOAD_STATUS.onLoad);
    }
  }, [basicDataProgressType, onBasicDataProgressChange]);

  useEffect(() => {
    if (hasClassFormatOptions) {
      basicDataReloadRequestedRef.current = false;
    }
  }, [hasClassFormatOptions]);

  useEffect(() => {
    if (
      basicDataProgressType === DATA_LOAD_STATUS.onLoad ||
      basicDataProgressType === DATA_LOAD_STATUS.onStart ||
      isTerminalBasicDataState ||
      docClassFormatList?.length > 0
    ) {
      return;
    }

    if (!basicDataReloadRequestedRef.current) {
      basicDataReloadRequestedRef.current = true;
      onBasicDataProgressChange(DATA_LOAD_STATUS.onLoad);
    }
  }, [
    basicDataProgressType,
    docClassFormatList,
    isTerminalBasicDataState,
    onBasicDataProgressChange,
  ]);

  useEffect(() => {
    if (!docClassFormatList || docClassFormatList.length === 0) {
      setClassOptions([]);
      setFilteredClassOptions([]);
      setFormattedData(null);
      return;
    }

    const classes = [];
    const classFormatMap = {};

    const extractAllClasses = (nodes, parentId = null) => {
        if (!Array.isArray(nodes)) return;
        nodes.forEach((node) => {
          const isClassNode =
            !parentId || (node.children && node.children.length > 0);

          if (isClassNode) {
            const classItem = {
              key: node.id,
              text: node.name,
              title: node.name,
              data: {
                id: node.id,
                code: node.code,
                viewRequired: node.viewRequired,
                formatRequired: node.formatRequired,
                objConRequired: node.objConRequired,
                hasFormats: node.children && node.children.length > 0,
                children: node.children || [],
              },
            };
            classes.push(classItem);

            if (node.children && node.children.length > 0) {
              classFormatMap[node.id] = node.children.map((child) => ({
                key: child.id,
                text: child.name,
                title: child.name,
                data: {
                  id: child.id,
                  code: child.code,
                  viewRequired: child.viewRequired,
                  formatRequired: child.formatRequired,
                  objConRequired: child.objConRequired,
                  parentClassId: node.id,
                },
              }));
            } else {
              classFormatMap[node.id] = [];
            }
          }

          if (node.children) {
            extractAllClasses(node.children, node.id);
          }
        });
      };

    extractAllClasses(docClassFormatList);
    setClassOptions(classes);
    setFilteredClassOptions(classes);
    setFormattedData(classFormatMap);
  }, [docClassFormatList]);

  // Keep dropdown selections in sync with persisted check-in state.
  // Formats are often loaded from get-docformatinfo (API), not only the class tree — restore both on remount.
  useEffect(() => {
    if (skipNextSyncRef.current) {
      skipNextSyncRef.current = false;
      return;
    }

    if (!classOptions || classOptions.length === 0 || !formattedData) {
      return;
    }

    if (!docClass) {
      return;
    }

    const classFromState =
      classOptions.find((option) => option?.data?.code === docClass) ?? null;
    if (!classFromState) {
      return;
    }

    const currentClassCode = selectedClass?.data?.code ?? "";
    const currentFormatCode = selectedFormat?.data?.code ?? "";
    if (
      currentClassCode === docClass &&
      (!docFormat || currentFormatCode === docFormat)
    ) {
      return;
    }

    const findFormatInList = (list) =>
      Array.isArray(list)
        ? list.find((option) => option?.data?.code === docFormat) ?? null
        : null;

    let cancelled = false;

    const syncClassFormatFromRedux = async () => {
      const classChanged = currentClassCode !== docClass;

      if (classChanged) {
        setSelectedClass(classFromState);
      }

      let formats = formattedData[classFromState.key] ?? [];
      const cachedFormats = classFormatsCache[docClass];
      if (Array.isArray(cachedFormats) && cachedFormats.length > 0) {
        formats = cachedFormats;
      }
      let formatFromState = docFormat ? findFormatInList(formats) : null;

      const hasLocalFormats = Array.isArray(formats) && formats.length > 0;
        const needsApiFormats = (!hasLocalFormats) || (docFormat && !formatFromState);
      if (needsApiFormats) {
        const shouldShowFormatLoading = !!docFormat;
        if (shouldShowFormatLoading) {
          setIsClassFormatsLoading(true);
        }
        try {
          const apiResult = await getFormatsByClass(docClass);
          if (cancelled) {
            return;
          }
          if (apiResult?.formats?.length > 0) {
            formats = apiResult.formats;
            classFormatsCache[docClass] = apiResult.formats;
            formatFromState = docFormat ? findFormatInList(formats) : null;
          }
          if (apiResult) {
            const classWithApiRules = {
              ...classFromState,
              data: {
                ...classFromState.data,
                viewRequired: apiResult.viewRequired ?? classFromState.data.viewRequired,
                formatRequired: apiResult.formatRequired ?? classFromState.data.formatRequired,
                objConRequired: apiResult.objConRequired ?? classFromState.data.objConRequired,
                restrictedAccess: apiResult.restrictedAccess ?? classFromState.data.restrictedAccess,
              },
            };
            setSelectedClass(classWithApiRules);
            onDocClassFormatChange(
              docClass,
              docFormat ?? "",
              classWithApiRules.data.viewRequired,
              classWithApiRules.data.formatRequired,
              classWithApiRules.data.objConRequired,
              uploadFiles,
              uploadViewCopyCount,
              { restrictedAccess: classWithApiRules.data.restrictedAccess ?? "N" },
            );
          }
        } catch (error) {
          console.error("Failed to load formats for Redux sync:", error);
        } finally {
          if (!cancelled) {
            setIsClassFormatsLoading(false);
          }
        }
      }

      if (cancelled) {
        return;
      }

      setFormatOptions(formats);
      setFilteredFormatOptions(formats);

      if (formatFromState && currentFormatCode !== docFormat) {
        setSelectedFormat(formatFromState);
      }
    };

    syncClassFormatFromRedux();

    return () => {
      cancelled = true;
    };
  }, [classOptions, formattedData, docClass, docFormat]);

  useEffect(() => {
    if (classSearchValue.trim() === "") {
      setFilteredClassOptions(classOptions);
    } else {
      const filtered = classOptions.filter((option) =>
        option.text.toLowerCase().includes(classSearchValue.toLowerCase()),
      );

      if (filtered.length === 0) {
        setFilteredClassOptions([
          {
            key: NO_RESULTS_CLASS_KEY,
            text: "No classes match your search",
            disabled: true,
          },
        ]);
      } else {
        setFilteredClassOptions(filtered);
      }
    }
  }, [classSearchValue, classOptions]);

  useEffect(() => {
    if (!selectedClass || !formatOptions) return;

    if (formatSearchValue.trim() === "") {
      setFilteredFormatOptions(formatOptions);
    } else {
      const filtered = formatOptions.filter((option) =>
        option.text.toLowerCase().includes(formatSearchValue.toLowerCase()),
      );

      if (filtered.length === 0) {
        setFilteredFormatOptions([
          {
            key: NO_RESULTS_FORMAT_KEY,
            text: "No formats match your search",
            disabled: true,
          },
        ]);
      } else {
        setFilteredFormatOptions(filtered);
      }
    }
  }, [formatSearchValue, formatOptions, selectedClass]);

  useLayoutEffect(() => {
    if (
      docClassFormatList?.length > 0 &&
      checkinNewStatus === ON_CHECKIN_NEW_STATUS.tabRefreh
    ) {
      cleanAll();
      setSelectedClass(null);
      setSelectedFormat(null);
      setFormatOptions([]);
      setFilteredFormatOptions([]);
      setClassSearchValue("");
      setFormatSearchValue("");
      onDocClassFormatChange("", "", "N", "N", "N", null, 0);
      onCheckinNewStatusChange(ON_CHECKIN_NEW_STATUS.completed);
    }
  }, [docClassFormatList, checkinNewStatus]);

  useEffect(() => {
    const loadBasicData = async () => {
      try {
        if (basicDataProgressType === DATA_LOAD_STATUS.onLoad) {
          await onBasicDataLoad({
            retry: 0,
            actionType: REFRESH_TOKEN_ACTION.documentBasicInfoLoad,
            refreshResponse: RESPONSE_TYPE.none,
          });
        }
      } catch (error) {
        console.error("Failed to load basic data:", error);
      }
    };

    loadBasicData();
  }, [basicDataProgressType]);

  useEffect(() => {
    prevClassRef.current = selectedClass;
  }, [selectedClass]);

  useEffect(() => {
    const run = async () => {
      if (triggerRefreshTokenPreAction(tokenRefreshPreActionInvoke) === true) {
        if (
          tokenRefreshPreActionInvoke.actionType ===
            REFRESH_TOKEN_ACTION.documentBasicInfoLoad &&
          basicDataProgressType === DATA_LOAD_STATUS.onLoad
        ) {
          await onBasicDataLoad({
            retry: tokenRefreshPreActionInvoke.retry,
            actionType: REFRESH_TOKEN_ACTION.documentBasicInfoLoad,
            refreshResponse: tokenRefreshPreActionInvoke.refreshResponse,
          });
        }
      }
    };

    run();
  }, [tokenRefreshPreActionInvoke]);

  function triggerRefreshTokenPreAction(tokenRefreshPreActionInvoke) {
    if (isValidPlayload(tokenRefreshPreActionInvoke)) {
      return (
        tokenRefreshPreActionInvoke.actionType ===
        REFRESH_TOKEN_ACTION.documentBasicInfoLoad
      );
    }

    return false;
  }

  const mapFormatsToOptions = (
    formats,
    viewRequiredValue,
    formatMandatoryValue,
    objectConnectionRequiredValue,
  ) => {
    if (!Array.isArray(formats)) return [];
    return formats.map((item) => ({
      key: `${item.docClass ?? item.DocClass ?? ""}${item.formatSize ?? item.FormatSize ?? ""}`,
      text: `${item.formatSize ?? item.FormatSize ?? ""} - ${item.formatSizeName ?? item.FormatSizeName ?? ""}`,
      title: `${item.formatSize ?? item.FormatSize ?? ""} - ${item.formatSizeName ?? item.FormatSizeName ?? ""}`,
      data: {
        id: `${item.docClass ?? item.DocClass ?? ""}${item.formatSize ?? item.FormatSize ?? ""}`,
        code: `${item.formatSize ?? item.FormatSize ?? ""}`,
        viewRequired: `${viewRequiredValue ?? "N"}`,
        formatRequired: `${formatMandatoryValue ?? "N"}`,
        objConRequired: `${objectConnectionRequiredValue ?? "N"}`,
      },
    }));
  };

  const getFormatsByClass = async (className) => {
    if (!className) {
      return null;
    }

    const payload = await onGetFormatsByClass(className);
    if (!payload) {
      return null;
    }
    const classItems = Array.isArray(payload)
      ? payload
      : payload?.classDetails ??
        payload?.ClassDetails ??
        payload?.value?.classDetails ??
        payload?.value?.ClassDetails ??
        [];

    if (!Array.isArray(classItems) || classItems.length === 0) {
      return null;
    }

    const classItem = classItems[0];
    const classFormats = classItem?.formats ?? classItem?.Formats ?? [];
    const apiViewRequired = `${classItem?.viewRequired ?? classItem?.ViewRequired ?? "N"}`;
    const apiFormatMandatory = `${classItem?.formatMandatory ?? classItem?.FormatMandatory ?? "N"}`;
    const apiObjConnectionRequired = `${classItem?.objectConnectionRequired ?? classItem?.ObjectConnectionRequired ?? "N"}`;
    const apiRestrictedAccess = `${classItem?.restrictedAccess ?? classItem?.RestrictedAccess ?? "N"}`;

    return {
      formats: mapFormatsToOptions(
        classFormats,
        apiViewRequired,
        apiFormatMandatory,
        apiObjConnectionRequired,
      ),
      viewRequired: apiViewRequired,
      formatRequired: apiFormatMandatory,
      objConRequired: apiObjConnectionRequired,
      restrictedAccess: apiRestrictedAccess,
    };
  };

  const handleClassChange = async (event, option) => {
    if (option?.disabled) {
      return;
    }
    if (option) {
      const previousFormatSelection = selectedFormat;

      setSelectedClass(option);
      // Prevent sync effect from immediately overwriting API-loaded formats
      // with stale local formattedData for the newly selected class.
      skipNextSyncRef.current = true;
      setClassSearchValue("");
      setSelectedFormat(null);
      setFormatSearchValue("");

      formatCode = "";
      formatCodeDisplay = "";

      let formats = (formattedData && formattedData[option.key]) || [];
      let classRuleValues = {
        viewRequired: option.data.viewRequired ?? "N",
        formatRequired: option.data.formatRequired ?? "N",
        objConRequired: option.data.objConRequired ?? "N",
        restrictedAccess: option.data.restrictedAccess ?? "N",
      };
      setIsClassFormatsLoading(true);
      try {
        const apiResult = await getFormatsByClass(option.data.code);
        if (apiResult && Array.isArray(apiResult.formats)) {
          if (apiResult.formats.length > 0) {
            formats = apiResult.formats;
            classFormatsCache[option.data.code] = apiResult.formats;
          }
          classRuleValues = {
            viewRequired: apiResult.viewRequired ?? classRuleValues.viewRequired,
            formatRequired: apiResult.formatRequired ?? classRuleValues.formatRequired,
            objConRequired: apiResult.objConRequired ?? classRuleValues.objConRequired,
            restrictedAccess: apiResult.restrictedAccess ?? classRuleValues.restrictedAccess,
          };
        }
      } catch (error) {
        console.error("Failed to load formats from get-docformatinfo:", error);
      } finally {
        setIsClassFormatsLoading(false);
      }

      setFormatOptions(formats);
        setFilteredFormatOptions(formats);

      classCode = option.data.code;
      viewRequired = classRuleValues.viewRequired;
      formatRequired = classRuleValues.formatRequired;
      objConRequired = classRuleValues.objConRequired;
      const restrictedAccess = classRuleValues.restrictedAccess;

      const selectedClassWithApiRules = {
        ...option,
        data: {
          ...option.data,
          viewRequired,
          formatRequired,
          objConRequired,
          restrictedAccess,
        },
      };
      setSelectedClass(selectedClassWithApiRules);

      onDocClassFormatChange(
        classCode,
        "",
        viewRequired,
        formatRequired,
        objConRequired,
        uploadFiles,
        uploadViewCopyCount,
        { restrictedAccess },
      );

      if (formats.length === 0) {
        const noFormatOption = {
          key: NO_FORMAT_AVAILABLE_KEY,
          text: "No formats available for this class",
          title: "No formats available for this class",
          disabled: true,
        };
        setFormatOptions([noFormatOption]);
        setFilteredFormatOptions([noFormatOption]);
      }
    } else {
      setSelectedClass(null);
      setSelectedFormat(null);
      setFormatOptions([]);
      setFilteredFormatOptions([]);
      setClassSearchValue("");
      setFormatSearchValue("");

      classCode = "";
      formatCode = "";
      formatCodeDisplay = "";
      viewRequired = "N";
      objConRequired = "N";
      formatRequired = "N";

      onDocClassFormatChange("", "", "N", "N", "N", null, 0);
    }
  };

  const handleFormatChange = (event, option) => {
    if (option?.disabled) {
      return;
    }
    if (option && option.key !== NO_FORMAT_AVAILABLE_KEY) {
      setSelectedFormat(option);
      setFormatSearchValue("");

      if (selectedClass) {
        formatCode = option.data.code;
        formatCodeDisplay = option.data.code;

        onDocClassFormatChange(
          selectedClass.data.code,
          formatCodeDisplay,
          selectedClass.data.viewRequired,
          selectedClass.data.formatRequired,
          selectedClass.data.objConRequired,
          uploadFiles,
          uploadViewCopyCount,
          { restrictedAccess: selectedClass.data.restrictedAccess ?? "N" },
        );
      }
    } else {
      setSelectedFormat(null);
      setFormatSearchValue("");
      formatCode = "";
      formatCodeDisplay = "";

      if (selectedClass) {
        onDocClassFormatChange(
          selectedClass.data.code,
          "",
          selectedClass.data.viewRequired,
          selectedClass.data.formatRequired,
          selectedClass.data.objConRequired,
          uploadFiles,
          uploadViewCopyCount,
          { restrictedAccess: selectedClass.data.restrictedAccess ?? "N" },
        );
      }
    }
  };

  const classFieldDisabled =
    isClassListLoading || filteredClassOptions.length === 0;
  const formatFieldDisabled =
    isClassFormatsLoading ||
    !selectedClass ||
    (filteredFormatOptions.length > 0 &&
      filteredFormatOptions[0].key === NO_FORMAT_AVAILABLE_KEY);

  const loadingDropdownColor =
    theme.colors.addovation70Green ?? theme.colors.themeDarkColor ?? "#4C8D6A";

  const classFormatDropdownFont = {
    fontSize: "12px",
    fontFamily: theme.fonts.regular,
  };

  const isDarkMode =
    String(theme.colors.pureColor || "").toLowerCase() === "#000000";
  const menuBg = theme.colors.backgroundColor ?? "#fff";
  const menuBorderColor = theme.colors.Addovation20Grey ?? "#d8d8d8";
  const optionHoverBg =
    theme.colors.msTeamsFilesHover ?? (isDarkMode ? "#343434" : "#EBEBEB");
  const optionSelectedBg = isDarkMode
    ? (theme.colors.dropDownSelectColor ?? "#3A3A3A")
    : (theme.colors.addovation10Grey ??
      theme.colors.Addovation20Grey ??
      "#EEEEEE");
  const scrollbarTrack =
    theme.colors.scrollbarBackground ?? (isDarkMode ? "#525252" : "#fff");
  const scrollbarThumb =
    theme.colors.scrollbarThumb ?? (isDarkMode ? "#848484" : "#ccc");

  const autocompleteSlotProps = {
    listbox: {
      sx: {
        maxHeight: 280,
        overflow: "auto",
        py: 0,
        fontSize: theme.fontSizes.menusize,
        fontFamily: theme.fonts.regular,
        backgroundColor: menuBg,
        color: theme.colors.fontColor,
        scrollbarWidth: "thin",
        scrollbarColor: `${scrollbarThumb} ${scrollbarTrack}`,
        "&::-webkit-scrollbar": {
          width: 10,
        },
        "&::-webkit-scrollbar-track": {
          background: scrollbarTrack,
        },
        "&::-webkit-scrollbar-thumb": {
          background: scrollbarThumb,
          borderRadius: "10px",
          border: `2px solid ${scrollbarTrack}`,
        },
        "& .MuiAutocomplete-option": {
          fontSize: theme.fontSizes.menusize,
          fontFamily: theme.fonts.regular,
          color: theme.colors.fontColor,
          "&:hover": {
            backgroundColor: optionHoverBg,
          },
          "&.Mui-focused": {
            backgroundColor: optionHoverBg,
          },
          '&[aria-selected="true"]': {
            backgroundColor: optionSelectedBg,
            color: theme.colors.fontColor,
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: optionHoverBg,
            color: theme.colors.fontColor,
          },
        },
      },
    },
    paper: {
      sx: {
        maxHeight: 300,
        overflow: "hidden",
        backgroundColor: menuBg,
        color: theme.colors.fontColor,
        border: `1px solid ${menuBorderColor}`,
        boxShadow: isDarkMode
          ? "0 4px 16px rgba(0, 0, 0, 0.45)"
          : "0 4px 16px rgba(0, 0, 0, 0.12)",
      },
    },
    popper: {
      sx: { zIndex: 100000 },
    },
  };

  const renderClassInput = (params) => (
    <TextField
      {...params}
      placeholder={isClassListLoading ? "Loading..." : "Select a Class"}
      aria-label="Document class"
      InputProps={{
        ...params.InputProps,
        startAdornment: isClassListLoading ? (
          <>
            <CircularProgress
              size={14}
              thickness={5}
              sx={{
                color: loadingDropdownColor,
                mr: 0.75,
                flexShrink: 0,
              }}
            />
            {params.InputProps.startAdornment}
          </>
        ) : (
          params.InputProps.startAdornment
        ),
      }}
    />
  );

  const renderFormatInput = (params) => (
    <TextField
      {...params}
      placeholder={isClassFormatsLoading ? "Loading..." : "Select a Format"}
      aria-label="Document format"
      InputProps={{
        ...params.InputProps,
        startAdornment: isClassFormatsLoading ? (
          <>
            <CircularProgress
              size={14}
              thickness={5}
              sx={{
                color: loadingDropdownColor,
                mr: 0.75,
                flexShrink: 0,
              }}
            />
            {params.InputProps.startAdornment}
          </>
        ) : (
          params.InputProps.startAdornment
        ),
      }}
    />
  );

  const autocompleteSx = (disabled, loading = false) => {
    const dropdownIndicatorColor = disabled
      ? (theme.colors.deactiveColor ?? "#939393")
      : (theme.colors.themeDarkColor ??
        theme.colors.addovation70Green ??
        "#4C8D6A");
    const placeholderColor =
      theme.colors.subTitle ??
      theme.colors.deactiveColor ??
      theme.colors.addovation30Gray ??
      "#B9B9B9";

    return {
    width: "100%",
    ...classFormatDropdownFont,
    "& .MuiAutocomplete-popupIndicator": {
      color: dropdownIndicatorColor,
    },
    "& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root": {
      color: "inherit",
    },
    "& .MuiAutocomplete-clearIndicator": {
      color: theme.colors.fontColor,
    },
    "& .MuiOutlinedInput-root": {
      minHeight: 38,
      ...classFormatDropdownFont,
      backgroundColor: disabled
        ? theme.colors.disableDropDownColor
        : theme.colors.backgroundColor,
      color: loading
        ? loadingDropdownColor
        : disabled
          ? placeholderColor
          : theme.colors.fontColor,
      "&.Mui-disabled": {
        opacity: 1,
      },
      "& .MuiInputBase-input": {
        ...classFormatDropdownFont,
        ...(loading
          ? {
              color: loadingDropdownColor,
              WebkitTextFillColor: loadingDropdownColor,
            }
          : disabled
            ? {
                color: placeholderColor,
                WebkitTextFillColor: placeholderColor,
              }
            : {}),
        "&::placeholder": {
          color: placeholderColor,
          opacity: 1,
        },
      },
      "&.Mui-disabled .MuiInputBase-input::placeholder": {
        color: placeholderColor,
        opacity: 1,
        WebkitTextFillColor: placeholderColor,
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.colors.Addovation20Grey ?? "#dcdcdc",
      },
      "&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.colors.themeDarkColor,
      },
      "&.Mui-focused:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline": {
        borderColor: theme.colors.themeDarkColor,
        borderWidth: "1px",
      },
    },
  };
  };

  const loaderBorderStyle = {
    padding: "10px autopx",
    width: "400px",
  };
  const messageBorderStyle = {
    margin: "auto",
    width: "200px",
    paddingLeft: "0px",
  };

  const renderClassFormatDotsLoader = () => (
    <div
      style={{
        padding: "4px 0",
        width: "100%",
        minHeight: 40,
        display: "flex",
        alignItems: "center",
      }}
    >
      <div style={{ width: 50, flexShrink: 0 }}>
        <Loader />
      </div>
    </div>
  );

  if (
    basicDataProgressType === DATA_LOAD_STATUS.completed &&
    !hasClassFormatOptions &&
    basicDataReloadRequestedRef.current &&
    !docClassFormatList?.length
  ) {
    return (
      <div style={loaderBorderStyle}>
        <div style={messageBorderStyle}>
          <IconButton
            style={{ marginLeft: "80px" }}
            iconProps={binIcon}
            title="Refresh"
            ariaLabel="Refresh"
            onClick={() => {
              basicDataReloadRequestedRef.current = false;
              onBasicDataProgressChange(DATA_LOAD_STATUS.onLoad);
            }}
          />
          <Label style={{ fontSize: "small", textAlign: "center" }}>
            {NO_CLASSFORMAT_FOUND}
          </Label>
        </div>
      </div>
    );
  }

  if (shouldShowClassFormatLoader) {
    return renderClassFormatDotsLoader();
  }

  const classError =
    showFieldValidation && !docClass?.trim() && !selectedClass
      ? CHECKIN_VALIDATION_CLASS_REQUIRED
      : "";
  const formatError =
    showFieldValidation &&
    isFormatRequired === true &&
    !docFormat?.trim() &&
    (selectedClass || docClass?.trim())
      ? CHECKIN_VALIDATION_FORMAT_REQUIRED
      : "";

  switch (basicDataProgressType) {
    case DATA_LOAD_STATUS.completed:
      return (
        <Stack
          horizontal
          tokens={{ childrenGap: 15 }}
          styles={{ root: { width: "100%" } }}
        >
          <Stack.Item styles={{ root: { width: "50%" } }}>
            <Autocomplete
              size="small"
              className="dropdown-365"
              disabled={classFieldDisabled}
              loading={isClassListLoading}
              options={filteredClassOptions}
              value={selectedClass}
              onChange={(event, newValue) => {
                if (newValue?.disabled) return;
                handleClassChange(event, newValue);
              }}
              inputValue={
                isClassListLoading
                  ? "Loading..."
                  : (classSearchValue || (selectedClass?.text ?? ""))
              }
              onInputChange={(event, newInputValue, reason) => {
                if (isClassListLoading) return;
                if (reason === "input" || reason === "clear") {
                  setClassSearchValue(newInputValue);
                }
              }}
              onOpen={() => {
                if (isClassListLoading) return;
                setClassMenuOpen(true);
                // Show full class list when opening the dropdown.
                // Keep search empty until the user starts typing.
                setClassSearchValue("");
              }}
              onClose={() => {
                setClassMenuOpen(false);
                setClassSearchValue("");
              }}
              filterOptions={(opts) => opts}
              getOptionLabel={(option) => option?.text ?? ""}
              isOptionEqualToValue={(a, b) => a?.key === b?.key}
              getOptionDisabled={(option) =>
                !!option.disabled || option.key === NO_RESULTS_CLASS_KEY
              }
              sx={autocompleteSx(classFieldDisabled, isClassListLoading)}
              slotProps={autocompleteSlotProps}
              renderInput={renderClassInput}
            />
            <FieldValidationMessage message={classError} />
          </Stack.Item>

          <Stack.Item styles={{ root: { width: "50%" } }}>
            <Autocomplete
              size="small"
              className="dropdown-365"
              disabled={formatFieldDisabled}
              loading={isClassFormatsLoading}
              options={filteredFormatOptions}
              value={selectedFormat}
              onChange={(event, newValue) => {
                if (newValue?.disabled) return;
                handleFormatChange(event, newValue);
              }}
              inputValue={
                isClassFormatsLoading
                  ? "Loading..."
                  : (formatSearchValue || (selectedFormat?.text ?? ""))
              }
              onInputChange={(event, newInputValue, reason) => {
                if (isClassFormatsLoading) return;
                if (reason === "input" || reason === "clear") {
                  setFormatSearchValue(newInputValue);
                }
              }}
              onOpen={() => {
                if (isClassFormatsLoading) return;
                setFormatMenuOpen(true);
                setFormatSearchValue("");
              }}
              onClose={() => {
                setFormatMenuOpen(false);
                setFormatSearchValue("");
              }}
              filterOptions={(opts) => opts}
              getOptionLabel={(option) => option?.text ?? ""}
              isOptionEqualToValue={(a, b) => a?.key === b?.key}
              getOptionDisabled={(option) =>
                !!option.disabled ||
                option.key === NO_RESULTS_FORMAT_KEY ||
                option.key === NO_FORMAT_AVAILABLE_KEY
              }
              sx={autocompleteSx(formatFieldDisabled, isClassFormatsLoading)}
              slotProps={autocompleteSlotProps}
              key={`format-autocomplete-${
                selectedClass ? selectedClass.key : "none"
              }`}
              renderInput={renderFormatInput}
            />
            <FieldValidationMessage message={formatError} />
          </Stack.Item>
        </Stack>
      );

    case DATA_LOAD_STATUS.noDate:
      return (
        <div style={loaderBorderStyle}>
          <div style={messageBorderStyle}>
            <IconButton
              style={{ marginLeft: "80px" }}
              iconProps={binIcon}
              title="Refresh"
              ariaLabel="Refresh"
              onClick={() => {
                onBasicDataProgressChange(DATA_LOAD_STATUS.onLoad);
              }}
            />
            <Label style={{ fontSize: "small", textAlign: "center" }}>
              {NO_CLASSFORMAT_FOUND}
            </Label>
          </div>
        </div>
      );

    case DATA_LOAD_STATUS.error:
    case DATA_LOAD_STATUS.ifsError:
      return (
        <div style={loaderBorderStyle}>
          <div style={messageBorderStyle}>
            <IconButton
              style={{ marginLeft: "80px" }}
              iconProps={binIcon}
              title="Refresh"
              ariaLabel="Refresh"
              onClick={() => {
                onBasicDataProgressChange(DATA_LOAD_STATUS.onLoad);
              }}
            />
            <Label
              style={{ fontSize: "small", color: "red", textAlign: "center" }}
            >
              {isValidPlayload(messageBoxInfo) &&
              isValidPlayload(messageBoxInfo.message)
                ? messageBoxInfo.message
                : IFS_SERVER_ERROR}
            </Label>
          </div>
        </div>
      );

    default:
      return null;
  }
};

export default ClassFormatTreeLoad;
